import React, { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
    AlertTriangle,
    Bell,
    BellRing,
    CheckCheck,
    CheckCircle2,
    Clock,
    Info,
    Pin,
    RefreshCw,
    Trash2,
    Wifi,
    WifiOff,
    X,
    XCircle,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useNotificationStore, type Notification as AppNotification } from '../lib/store';
import { useTaskReminderNotifications } from '../hooks/useTaskReminderNotifications';

const HISTORY_STORAGE_KEY = 'focusflow.notification-history.v1';

const loadHistory = (): AppNotification[] => {
    if (typeof window === 'undefined') return [];
    try {
        const raw = window.localStorage.getItem(HISTORY_STORAGE_KEY);
        if (!raw) return [];
        return JSON.parse(raw) as AppNotification[];
    } catch {
        return [];
    }
};

const saveHistory = (history: AppNotification[]) => {
    if (typeof window === 'undefined') return;
    try {
        window.localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(history.slice(0, 30)));
    } catch {
        // ignore storage issues
    }
};

export const NotificationSystem: React.FC = () => {
    const { notifications, removeNotification, alarmTriggered, triggerAlarm, clearAll } = useNotificationStore();
    const {
        browserPermission,
        isOnline,
        pendingCount,
        nextReminder,
        overdueReminders,
        soonReminders,
        requestBrowserPermission,
        refreshReminders,
    } = useTaskReminderNotifications();
    const [panelOpen, setPanelOpen] = useState(false);
    const [history, setHistory] = useState<AppNotification[]>(() => loadHistory());
    const [activeTab, setActiveTab] = useState<'recent' | 'reminders' | 'settings'>('recent');

    useEffect(() => {
        if (notifications.length === 0) return;

        setHistory((current) => {
            const merged = [...notifications, ...current];
            const deduped = Array.from(new Map(merged.map((item) => [item.id, item])).values())
                .sort((a, b) => b.timestamp.localeCompare(a.timestamp))
                .slice(0, 30);
            saveHistory(deduped);
            return deduped;
        });
    }, [notifications]);

    useEffect(() => {
        saveHistory(history);
    }, [history]);

    const liveCount = notifications.length;
    const reminderCount = overdueReminders.length + soonReminders.length;
    const badgeCount = Math.max(liveCount, pendingCount);
    const recentNotifications = useMemo(() => history.slice(0, 12), [history]);

    const notificationIcon = (type: AppNotification['type']) => {
        if (type === 'success') return <CheckCircle2 className="h-4 w-4" />;
        if (type === 'error') return <XCircle className="h-4 w-4" />;
        if (type === 'warning') return <AlertTriangle className="h-4 w-4" />;
        if (type === 'revision') return <Bell className="h-4 w-4" />;
        return <Info className="h-4 w-4" />;
    };

    const notificationTone = (type: AppNotification['type']) => {
        if (type === 'success') return 'border-emerald-500/20 bg-emerald-500/10 text-emerald-300';
        if (type === 'error') return 'border-rose-500/20 bg-rose-500/10 text-rose-300';
        if (type === 'warning') return 'border-amber-500/20 bg-amber-500/10 text-amber-300';
        if (type === 'revision') return 'border-violet-500/20 bg-violet-500/10 text-violet-300';
        return 'border-white/10 bg-white/5 text-slate-200';
    };

    const relativeTime = (timestamp: string) => {
        try {
            return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
        } catch {
            return 'just now';
        }
    };

    return (
        <>
            {!isOnline && (
                <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    className="fixed left-1/2 top-4 z-[115] w-[min(92vw,760px)] -translate-x-1/2 rounded-2xl border border-amber-500/20 bg-slate-950/90 px-4 py-3 text-sm text-amber-100 shadow-2xl backdrop-blur-xl"
                >
                    <div className="flex items-center gap-3">
                        <WifiOff className="h-4 w-4 shrink-0 text-amber-300" />
                        <div className="min-w-0 flex-1">
                            <p className="font-semibold">You are offline</p>
                            <p className="text-xs text-amber-100/80">The app shell stays available and reminders will resume when you're back online.</p>
                        </div>
                    </div>
                </motion.div>
            )}

            {/* Alarm Overlay */}
            <AnimatePresence>
                {alarmTriggered && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-md p-6"
                    >
                        <motion.div
                            initial={{ scale: 0.9, y: 20 }}
                            animate={{ scale: 1, y: 0 }}
                            className="w-full max-w-md bg-[#161B22] border border-[#7C3AED]/30 rounded-3xl p-8 text-center shadow-2xl"
                        >
                            <div className="w-20 h-20 rounded-full bg-[#7C3AED]/20 flex items-center justify-center mx-auto mb-6 relative">
                                <Clock className="w-10 h-10 text-[#7C3AED] animate-pulse" />
                                <div className="absolute inset-0 rounded-full border-2 border-[#7C3AED] animate-ping opacity-50" />
                            </div>
                            <h2 className="text-3xl font-bold mb-2">Time's Up!</h2>
                            <p className="text-[#94A3B8] mb-8">Your focus session has completed successfully. Take a well-deserved break!</p>
                            <button
                                onClick={() => triggerAlarm(false)}
                                className="w-full py-4 rounded-2xl bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-bold transition-all shadow-lg shadow-[#7C3AED]/20 active:scale-[0.98]"
                            >
                                Dismiss Alarm
                            </button>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            <AnimatePresence>
                <motion.button
                    type="button"
                    onClick={() => setPanelOpen((current) => !current)}
                    className="fixed bottom-6 right-6 z-[100] flex items-center gap-3 rounded-full border border-white/10 bg-slate-950/90 px-4 py-3 text-sm font-semibold text-white shadow-2xl backdrop-blur-xl transition-transform hover:scale-[1.02]"
                >
                    <div className="relative">
                        {pendingCount > 0 ? <BellRing className="h-5 w-5 text-violet-300" /> : <Bell className="h-5 w-5 text-white" />}
                        {badgeCount > 0 && (
                            <span className="absolute -right-2 -top-2 min-w-5 rounded-full bg-violet-500 px-1.5 py-0.5 text-[10px] font-black text-white">
                                {badgeCount > 9 ? '9+' : badgeCount}
                            </span>
                        )}
                    </div>
                    <span className="hidden sm:inline">Notifications</span>
                </motion.button>
            </AnimatePresence>

            <AnimatePresence>
                {panelOpen && (
                    <motion.aside
                        initial={{ opacity: 0, x: 24 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 24 }}
                        className="fixed bottom-20 right-4 z-[105] w-[min(92vw,420px)] overflow-hidden rounded-[28px] border border-white/10 bg-[#07111e]/95 shadow-2xl backdrop-blur-xl"
                    >
                        <div className="border-b border-white/10 bg-white/[0.03] p-4">
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <p className="text-xs font-black uppercase tracking-[0.28em] text-[#94A3B8]">Command Center</p>
                                    <h3 className="mt-1 text-xl font-bold">Live Notifications</h3>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setPanelOpen(false)}
                                    className="rounded-xl border border-white/10 bg-white/5 p-2 text-[#94A3B8] transition-colors hover:text-white"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            </div>

                            <div className="mt-4 flex flex-wrap gap-2">
                                {(['recent', 'reminders', 'settings'] as const).map((tab) => (
                                    <button
                                        key={tab}
                                        type="button"
                                        onClick={() => setActiveTab(tab)}
                                        className={`rounded-full border px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors ${activeTab === tab
                                            ? 'border-violet-500/30 bg-violet-500/20 text-white'
                                            : 'border-white/10 bg-white/5 text-[#94A3B8] hover:text-white'
                                            }`}
                                    >
                                        {tab}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="max-h-[70vh] overflow-y-auto p-4">
                            {activeTab === 'recent' && (
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between text-xs text-[#94A3B8]">
                                        <span>{recentNotifications.length} recent items</span>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                clearAll();
                                                setHistory([]);
                                            }}
                                            className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-[#CBD5E1] transition-colors hover:text-white"
                                        >
                                            <CheckCheck className="h-3.5 w-3.5" />
                                            Clear all
                                        </button>
                                    </div>

                                    {recentNotifications.length === 0 ? (
                                        <div className="rounded-[22px] border border-dashed border-white/10 px-4 py-8 text-center text-sm text-[#64748B]">
                                            Your notification history will appear here.
                                        </div>
                                    ) : (
                                        recentNotifications.map((item) => (
                                            <div
                                                key={item.id}
                                                className={`rounded-[22px] border p-4 ${notificationTone(item.type)}`}
                                            >
                                                <div className="flex items-start gap-3">
                                                    <div className="mt-0.5 rounded-xl border border-white/10 bg-white/5 p-2">
                                                        {notificationIcon(item.type)}
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <div className="flex items-start justify-between gap-3">
                                                            <div className="min-w-0">
                                                                <h4 className="truncate text-sm font-bold">{item.title}</h4>
                                                                <p className="mt-1 text-xs leading-relaxed text-white/80">{item.message}</p>
                                                            </div>
                                                            <button
                                                                type="button"
                                                                onClick={() => setHistory((current) => current.filter((entry) => entry.id !== item.id))}
                                                                className="rounded-lg p-1 text-white/70 transition-colors hover:bg-white/10 hover:text-white"
                                                            >
                                                                <Trash2 className="h-3.5 w-3.5" />
                                                            </button>
                                                        </div>
                                                        <p className="mt-3 text-[11px] uppercase tracking-[0.22em] text-white/50">
                                                            {relativeTime(item.timestamp)}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            )}

                            {activeTab === 'reminders' && (
                                <div className="space-y-3">
                                    <div className="rounded-[22px] border border-white/10 bg-white/[0.03] p-4">
                                        <div className="flex items-center justify-between gap-3">
                                            <div>
                                                <p className="text-xs font-black uppercase tracking-[0.24em] text-[#94A3B8]">Browser alerts</p>
                                                <p className="mt-1 text-sm text-[#CBD5E1]">
                                                    {browserPermission === 'granted'
                                                        ? 'Desktop notifications are enabled.'
                                                        : browserPermission === 'denied'
                                                            ? 'Desktop notifications are blocked.'
                                                            : 'Desktop notifications are available if you want them.'}
                                                </p>
                                            </div>
                                            {browserPermission !== 'granted' && browserPermission !== 'unsupported' && (
                                                <button
                                                    type="button"
                                                    onClick={() => void requestBrowserPermission()}
                                                    className="rounded-2xl bg-violet-500 px-4 py-2 text-sm font-semibold text-white"
                                                >
                                                    Enable
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="rounded-[22px] border border-white/10 bg-white/[0.03] p-4">
                                            <p className="text-xs font-black uppercase tracking-[0.24em] text-[#94A3B8]">Due Soon</p>
                                            <p className="mt-2 text-3xl font-black text-white">{soonReminders.length}</p>
                                        </div>
                                        <div className="rounded-[22px] border border-white/10 bg-white/[0.03] p-4">
                                            <p className="text-xs font-black uppercase tracking-[0.24em] text-[#94A3B8]">Overdue</p>
                                            <p className="mt-2 text-3xl font-black text-white">{overdueReminders.length}</p>
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        {nextReminder ? (
                                            <div className="rounded-[22px] border border-violet-500/20 bg-violet-500/10 p-4">
                                                <p className="text-xs font-black uppercase tracking-[0.24em] text-violet-200">Next Reminder</p>
                                                <h4 className="mt-1 text-base font-bold text-white">{nextReminder.task.title}</h4>
                                                <p className="mt-1 text-sm text-violet-100/80">
                                                    {nextReminder.bucket === 'due'
                                                        ? `${Math.abs(nextReminder.minutesUntil)} minute${Math.abs(nextReminder.minutesUntil) === 1 ? '' : 's'} overdue`
                                                        : `In ${nextReminder.minutesUntil} minute${nextReminder.minutesUntil === 1 ? '' : 's'}`}
                                                </p>
                                            </div>
                                        ) : (
                                            <div className="rounded-[22px] border border-dashed border-white/10 px-4 py-8 text-center text-sm text-[#64748B]">
                                                No active reminders right now.
                                            </div>
                                        )}
                                    </div>

                                    <button
                                        type="button"
                                        onClick={refreshReminders}
                                        className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm font-semibold text-[#CBD5E1] transition-colors hover:border-white/20 hover:text-white"
                                    >
                                        <RefreshCw className="h-4 w-4" />
                                        Refresh reminder scan
                                    </button>
                                </div>
                            )}

                            {activeTab === 'settings' && (
                                <div className="space-y-3">
                                    <div className="rounded-[22px] border border-white/10 bg-white/[0.03] p-4">
                                        <div className="flex items-center gap-3">
                                            {isOnline ? <Wifi className="h-5 w-5 text-emerald-300" /> : <WifiOff className="h-5 w-5 text-amber-300" />}
                                            <div>
                                                <p className="text-sm font-semibold text-white">{isOnline ? 'Online' : 'Offline'}</p>
                                                <p className="text-xs text-[#94A3B8]">
                                                    {isOnline ? 'Network checks are active.' : 'Using local app-shell support.'}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="rounded-[22px] border border-white/10 bg-white/[0.03] p-4">
                                        <p className="text-xs font-black uppercase tracking-[0.24em] text-[#94A3B8]">Live toasts</p>
                                        <p className="mt-2 text-3xl font-black text-white">{liveCount}</p>
                                        <p className="mt-2 text-sm text-[#94A3B8]">Toast notifications auto-dismiss after a few seconds, but the history stays here.</p>
                                    </div>

                                    <button
                                        type="button"
                                        onClick={() => {
                                            clearAll();
                                            setHistory([]);
                                        }}
                                        className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-rose-500/20 bg-rose-500/10 px-4 py-3 text-sm font-semibold text-rose-200 transition-colors hover:bg-rose-500/20"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                        Reset notification history
                                    </button>
                                </div>
                            )}
                        </div>
                    </motion.aside>
                )}
            </AnimatePresence>

            {/* Toast Notifications */}
            <div className="pointer-events-none fixed right-6 top-6 z-[90] flex flex-col gap-3">
                <AnimatePresence>
                    {notifications.map((n) => (
                        <motion.div
                            key={n.id}
                            initial={{ opacity: 0, x: 20, scale: 0.95 }}
                            animate={{ opacity: 1, x: 0, scale: 1 }}
                            exit={{ opacity: 0, x: 20, scale: 0.95 }}
                            className="pointer-events-auto"
                        >
                            <div className={`notification-toast flex w-80 items-start gap-3 rounded-2xl border p-4 shadow-xl backdrop-blur-xl ${n.type === 'success' ? 'bg-[#10B981]/10 border-[#10B981]/20' :
                                n.type === 'error' ? 'bg-[#EF4444]/10 border-[#EF4444]/20' :
                                    n.type === 'warning' ? 'bg-[#F59E0B]/10 border-[#F59E0B]/20' :
                                        n.type === 'revision' ? 'bg-[#7C3AED]/10 border-[#7C3AED]/20' :
                                            'bg-white/5 border-white/10'
                                }`}>
                                <div className={`mt-0.5 rounded-xl p-2 ${n.type === 'success' ? 'bg-[#10B981]/20 text-[#10B981]' :
                                    n.type === 'error' ? 'bg-[#EF4444]/20 text-[#EF4444]' :
                                        n.type === 'warning' ? 'bg-[#F59E0B]/20 text-[#F59E0B]' :
                                            n.type === 'revision' ? 'bg-[#7C3AED]/20 text-[#7C3AED]' :
                                                'bg-white/10 text-white'
                                    }`}>
                                    {notificationIcon(n.type)}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h4 className="text-sm font-bold truncate">{n.title}</h4>
                                    <p className="text-xs text-[#94A3B8] leading-relaxed mt-0.5">{n.message}</p>
                                </div>
                                <button
                                    onClick={() => removeNotification(n.id)}
                                    className="p-1 rounded-lg hover:bg-white/5 text-[#94A3B8] transition-colors"
                                >
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        </motion.div>
                    ))}
                </AnimatePresence>
            </div>
        </>
    );
};
